import { DecodedFrame } from "@/features/ble/utils/decodeNotification.ts";
import { sortBy } from "lodash";
import {
  BLUFI_TYPE_DATA,
  BLUFI_TYPE_DATA_SUBTYPE_CUSTOM_DATA,
  BLUFI_TYPE_DATA_SUBTYPE_ERROR_INFO,
  BLUFI_TYPE_DATA_SUBTYPE_STA_BSSID,
  BLUFI_TYPE_DATA_SUBTYPE_STA_MAX_CONN_RETRY,
  BLUFI_TYPE_DATA_SUBTYPE_STA_SSID,
  BLUFI_TYPE_DATA_SUBTYPE_WIFI_LIST,
  BLUFI_TYPE_DATA_SUBTYPE_WIFI_REP,
} from "@/services/blufi-constants.ts";

export const assembleFragments = (fragments: DecodedFrame[]) => {
  console.log("assembleFragments");

  sortBy(fragments, "sequence");
  // TODO make sure there's no hole in the sequence

  if (fragments[0].type !== BLUFI_TYPE_DATA) {
    throw new Error(`Unsupported fragment type ${fragments[0].type}`);
  }

  const isSingleFrame = fragments.length === 1;

  const totalLength = isSingleFrame
    ? fragments[0].frame[3]
    : fragments[0].frame[4] + 256 * fragments[0].frame[5];

  const fullFrame = new Uint8Array(totalLength);

  let frameOffset = 0;

  for (const fragment of fragments) {
    const fragmentData = fragment.frame.subarray(
      fragment.hasSubsequentFragments ? 6 : 4
    );
    fullFrame.set(fragmentData, frameOffset);
    frameOffset += fragment.len - (fragment.hasSubsequentFragments ? 2 : 0);
  }

  switch (fragments[0].subType) {
    case BLUFI_TYPE_DATA_SUBTYPE_CUSTOM_DATA:
      return parseCustomData(fullFrame);
    case BLUFI_TYPE_DATA_SUBTYPE_WIFI_LIST:
      return parseWifiList(fullFrame);
    case BLUFI_TYPE_DATA_SUBTYPE_WIFI_REP:
      return parseWifiStatus(fullFrame);
    case BLUFI_TYPE_DATA_SUBTYPE_ERROR_INFO:
      return parseError(fullFrame);
  }

  console.log(`Unsupported subtype: ${fragments[0].subType}`);
};

const parseCustomData = (fullFrame: Uint8Array) => {
  console.log("parseCustomData");
  const stringValue = String.fromCharCode(...fullFrame);
  let replacedString = stringValue
    .split("")
    .map((char) => {
      const charCode = char.charCodeAt(0);
      return charCode === 0
        ? " "
        : charCode < 32 || charCode >= 127
        ? "."
        : char;
    })
    .join("");

  if (!replacedString.startsWith("{")) {
    replacedString = '{"' + replacedString;
  }

  console.log("decodedString", replacedString);

  const parsed = JSON.parse(replacedString);
  console.log("parsedString", parsed);

  return parsed;
};

const parseWifiList = (fullFrame: Uint8Array) => {
  console.log("parseWifiList");

  let index = 0;
  let remainingLength = fullFrame.length;
  const scannedWifiDevices = [];
  while (remainingLength > 0) {
    let ssidLen = fullFrame[index++] - 1;
    let rssi = fullFrame[index++];
    if (rssi > 127) rssi -= 256;
    remainingLength -= 2;
    let ssid = "";
    while (ssidLen > 0) {
      ssid += String.fromCharCode(fullFrame[index++]);
      remainingLength--;
      ssidLen--;
    }
    console.log(`SSID=${ssid}, rssi=${rssi}dBm`);
    scannedWifiDevices.push({ ssid: ssid, rssi: rssi });
  }
  console.log("scannedWifiDevices", scannedWifiDevices);
  return { response: scannedWifiDevices };
};

export const parseWifiStatus = (fullFrame: Uint8Array) => {
  console.log("parseWifiStatus");
  const status = {
    opMode: fullFrame[0] as number,
    conState: fullFrame[1] as number,
    conNum: fullFrame[2] as number,
    bssid: undefined as string | undefined,
    ssid: undefined as string | undefined,
    maxConnRetry: undefined as number | undefined,
    rest: undefined as string | undefined,
  };

  let idx = 3;
  while (idx < fullFrame.length) {
    switch (fullFrame[idx]) {
      case BLUFI_TYPE_DATA_SUBTYPE_STA_BSSID:
        status.bssid = Array.from(
          fullFrame.subarray(idx + 2, idx + 2 + fullFrame[idx + 1])
        )
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join(" ");
        idx += fullFrame[idx + 1] + 2;
        break;
      case BLUFI_TYPE_DATA_SUBTYPE_STA_SSID:
        status.ssidNumber = Array.from(
          fullFrame.subarray(idx + 2, idx + 2 + fullFrame[idx + 1])
        )
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join(" ");
        status.ssid = String.fromCharCode(
          ...fullFrame.subarray(idx + 2, idx + 2 + fullFrame[idx + 1])
        );
        idx += fullFrame[idx + 1] + 2;
        break;
      case BLUFI_TYPE_DATA_SUBTYPE_STA_MAX_CONN_RETRY:
        status.maxConnRetry = Array.from(
          fullFrame.subarray(idx + 2, idx + 2 + fullFrame[idx + 1])
        )
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join(" ");
        idx += fullFrame[idx + 1] + 2;
        break;
      default:
        status.rest =
          "<br>?" +
          fullFrame[idx] +
          "?: " +
          Array.from(fullFrame.subarray(idx + 2, idx + 2 + fullFrame[idx + 1]))
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join(" ");
        idx += fullFrame[idx + 1] + 2;
        break;
    }
  }

  return { response: status };
};

const parseError = (fullFrame: Uint8Array) => {
  const data = fullFrame[0];
  const errorTypes = [
    "0x00: sequence error",
    "0x01: checksum error",
    "0x02: decrypt error",
    "0x03: encrypt error",
    "0x04: init security error",
    "0x05: dh malloc error",
    "0x06: dh param error",
    "0x07: read param error",
    "0x08: make public error",
    "0x09: data format error",
    "0x0a: calculate MD5 error",
    "0x0b: Wi-Fi scan error",
  ];
  throw new Error(`Received BluFi error ${data} (${errorTypes[data]})`);
};
